import { useState } from 'react'
import type { StageAllocation } from '../../types/strategy'
import type { WalletBalance } from '../../types/wallet'
import type { LpState } from '../../types/lp'
import { STAGE_RECIPES } from '../capital/stageStaticSteps'
import { HealthGauge } from './HealthGauge'
import { LpRangeViz } from './LpRangeViz'
import StakeForm from '../trade/StakeForm'
import BorrowForm from '../trade/BorrowForm'
import CollateralForm from '../trade/CollateralForm'
import HedgeForm from '../trade/HedgeForm'
import LpForm from '../trade/LpForm'
import TokenIcon from '../common/TokenIcon'
import { MINTS } from '../../api/trade'

function usd(n: number | null | undefined) {
  if (n == null) return '—'
  return '$' + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
function pct(v: number | null | undefined, d = 1) {
  if (v == null) return '—'
  return (v * 100).toFixed(d) + '%'
}
function fmtNum(n: number | null | undefined, d = 4) {
  if (n == null) return '—'
  return n.toLocaleString('en-US', { maximumFractionDigits: d })
}

const STAGE_ICON_BG: Record<string, string> = {
  STAKE: 'bg-emerald-500/10 border-emerald-500/20',
  LEND: 'bg-accent-blue/10 border-accent-blue/20',
  BORROW: 'bg-amber-500/10 border-amber-500/20',
  LP: 'bg-accent-purple/10 border-accent-purple/20',
  HEDGE: 'bg-red-500/10 border-red-500/20',
}
const STAGE_TEXT: Record<string, string> = {
  STAKE: 'text-emerald-400', LEND: 'text-accent-blue', BORROW: 'text-amber-400',
  LP: 'text-accent-purple', HEDGE: 'text-red-400',
}

interface Props {
  stageKey: string
  stage: StageAllocation
  basePrice: number
  projectedHf: number
  balances: WalletBalance[] | null
  lpPositions?: LpState[] | null
  onNext: () => void
  onBack: () => void
  isLast: boolean
}

export function WizardStageStep({ stageKey, stage, basePrice, projectedHf, balances, lpPositions, onNext, onBack, isLast }: Props) {
  const recipe = STAGE_RECIPES[stageKey]
  const [iframeLoaded, setIframeLoaded] = useState(false)
  const [iframeBlocked, setIframeBlocked] = useState<boolean>(recipe?.framable === false)
  const [copied, setCopied] = useState<string | null>(null)

  const copy = (tag: string, text: string) => {
    navigator.clipboard.writeText(text.replace(/[^0-9.]/g, '')).then(() => {
      setCopied(tag)
      window.setTimeout(() => setCopied(null), 1500)
    }).catch(() => {})
  }

  const NativeForm =
    stageKey === 'STAKE'  ? StakeForm  :
    stageKey === 'BORROW' ? BorrowForm :
    stageKey === 'HEDGE'  ? HedgeForm  :
    null

  // STAKE: no prefill — user decides how much SOL to stake
  // LEND: prefill full jupSOL wallet balance
  // BORROW: prefill the recommended borrow amount (USD)
  // HEDGE: handled separately via defaultSolAmount below
  const prePopulatedAmount: string | undefined =
    stageKey === 'BORROW' ? stage.amountUsd.toFixed(2) :
    undefined

  const hedgeSolAmount: string | undefined =
    stageKey === 'HEDGE' && stage.primaryAssetAmount != null
      ? stage.primaryAssetAmount.toFixed(4)
      : undefined

  // Build pre-populated rows
  const rows: { label: string; value: string; copyTag?: string }[] = []
  if (stageKey === 'LP') {
    rows.push({ label: 'Total position', value: usd(stage.amountUsd), copyTag: 'total' })
    if (stage.lpSolRatio != null && stage.lpStableRatio != null) {
      rows.push({ label: 'SOL leg',   value: usd(stage.amountUsd * stage.lpSolRatio) })
      rows.push({ label: 'USDC leg',  value: usd(stage.amountUsd * stage.lpStableRatio) })
    }
    if (stage.lpRangeLow  != null) rows.push({ label: 'Range low',  value: usd(stage.lpRangeLow) })
    if (stage.lpRangeHigh != null) rows.push({ label: 'Range high', value: usd(stage.lpRangeHigh) })
  } else if (stageKey === 'HEDGE') {
    if (stage.primaryAssetAmount != null) {
      rows.push({ label: 'Hedge size', value: fmtNum(stage.primaryAssetAmount) + ' SOL', copyTag: 'base' })
      rows.push({ label: 'Notional (USD)', value: usd(stage.amountUsd) })
    } else {
      rows.push({ label: 'Notional', value: usd(stage.amountUsd), copyTag: 'notional' })
    }
    if (stage.leverageFactor != null) rows.push({ label: 'Leverage', value: fmtNum(stage.leverageFactor, 2) + '×' })
    if (stage.stopLossPrice  != null) rows.push({ label: 'Stop-loss', value: usd(stage.stopLossPrice) })
  } else {
    rows.push({ label: 'Amount (USD)', value: usd(stage.amountUsd), copyTag: 'usd' })
    if (stage.primaryAssetAmount != null && stage.primaryAssetSymbol) {
      rows.push({
        label: `Amount (${stage.primaryAssetSymbol})`,
        value: fmtNum(stage.primaryAssetAmount) + ' ' + stage.primaryAssetSymbol,
        copyTag: 'base',
      })
    }
  }
  rows.push({ label: 'APY', value: pct(stage.yieldOrCostApr) })

  return (
    <div className="space-y-5">
      {/* Stage hero */}
      <div className={`rounded-xl border p-5 ${STAGE_ICON_BG[stageKey] ?? 'bg-white/[0.03] border-white/5'}`}>
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-xl border flex items-center justify-center shrink-0 ${STAGE_ICON_BG[stageKey]}`}>
            <span className={`text-xl font-bold ${STAGE_TEXT[stageKey]}`}>
              {recipe?.title?.charAt(0) ?? stageKey.charAt(0)}
            </span>
          </div>
          <div>
            <h2 className={`text-lg font-bold ${STAGE_TEXT[stageKey]}`}>{recipe?.title ?? stageKey}</h2>
            <p className="text-sm text-gray-300 mt-0.5">{recipe?.subtitle}</p>
          </div>
        </div>

        {/* Stage-specific hero visual */}
        {stageKey === 'STAKE' && (
          <div className="flex items-center justify-center gap-6 mt-5">
            <div className="flex flex-col items-center gap-2">
              <TokenIcon symbol="SOL" mint={MINTS.SOL} size={36} />
              <span className="text-xs text-gray-400">SOL</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <svg viewBox="0 0 40 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="w-10 h-4 text-emerald-400">
                <line x1="2" y1="8" x2="32" y2="8" /><polyline points="26 3 32 8 26 13" />
              </svg>
              <span className="text-[10px] text-emerald-400 font-semibold">{pct(stage.yieldOrCostApr)} APY</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <TokenIcon symbol="jupSOL" mint={MINTS.jupSOL} size={36} />
              <span className="text-xs text-emerald-400 font-medium">jupSOL</span>
            </div>
          </div>
        )}

        {stageKey === 'LEND' && (
          <div className="flex items-center justify-center gap-4 mt-5 text-center">
            <div className="flex flex-col items-center gap-1">
              <TokenIcon symbol="jupSOL" mint={MINTS.jupSOL} size={32} />
              <span className="text-xs text-gray-400">jupSOL</span>
            </div>
            <svg viewBox="0 0 40 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="w-10 h-4 text-accent-blue"><line x1="2" y1="8" x2="32" y2="8" /><polyline points="26 3 32 8 26 13" /></svg>
            <div className="flex flex-col items-center gap-1">
              <div className="w-8 h-8 rounded-lg bg-accent-blue/20 border border-accent-blue/30 flex items-center justify-center text-xs font-bold text-accent-blue">JL</div>
              <span className="text-xs text-gray-400">Jupiter Lend</span>
            </div>
            <svg viewBox="0 0 40 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="w-10 h-4 text-accent-blue"><line x1="2" y1="8" x2="32" y2="8" /><polyline points="26 3 32 8 26 13" /></svg>
            <div className="flex flex-col items-center gap-1">
              <div className="text-lg font-bold text-accent-blue">{pct(stage.yieldOrCostApr)}</div>
              <span className="text-xs text-accent-blue/70">APY + collateral</span>
            </div>
          </div>
        )}

        {stageKey === 'BORROW' && (
          <div className="mt-5 flex justify-center">
            <HealthGauge healthFactor={projectedHf} />
          </div>
        )}

        {stageKey === 'LP' && stage.lpRangeLow != null && stage.lpRangeHigh != null && (
          <div className="mt-4">
            <LpRangeViz
              rangeLow={stage.lpRangeLow}
              rangeHigh={stage.lpRangeHigh}
              currentPrice={basePrice}
            />
          </div>
        )}

        {stageKey === 'HEDGE' && (
          <div className="flex items-center justify-center gap-6 mt-5">
            <div className="text-center">
              <div className="text-lg font-bold text-red-400">SHORT</div>
              <div className="text-xs text-gray-400">SOL perp</div>
            </div>
            <svg viewBox="0 0 16 40" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="w-4 h-10 text-gray-500"><line x1="8" y1="2" x2="8" y2="32" /><polyline points="3 26 8 32 13 26" /></svg>
            <div className="text-center">
              <div className="text-sm font-semibold text-gray-200">If SOL drops</div>
              <div className="text-lg font-bold text-emerald-400">Profit</div>
              <div className="text-xs text-gray-400">offsets LP loss</div>
            </div>
          </div>
        )}

      </div>

      {/* Pre-populated values */}
      <div className="rounded-xl border border-white/5 bg-surface-card p-4">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-3">Your amounts (pre-calculated)</div>
        <div className="space-y-2">
          {rows.map(r => (
            <div key={r.label} className="flex items-center gap-2">
              <span className="text-[11px] text-gray-500 flex-1">{r.label}</span>
              <span className="text-sm font-semibold text-white tabular-nums">{r.value}</span>
              {r.copyTag && !NativeForm && (
                <button
                  type="button"
                  onClick={() => copy(r.copyTag!, r.value)}
                  className="text-[10px] text-accent-blue hover:text-blue-300 bg-accent-blue/10 hover:bg-accent-blue/20 rounded px-1.5 py-0.5 transition-colors"
                >
                  {copied === r.copyTag ? 'Copied!' : 'Copy'}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Protocol form or iframe */}
      {stageKey === 'LEND' ? (
        <div className="rounded-xl border border-white/5 bg-surface-card p-4">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-3">Execute on-chain</div>
          <CollateralForm balances={balances} />
        </div>
      ) : stageKey === 'HEDGE' ? (
        <div className="rounded-xl border border-white/5 bg-surface-card p-4">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-3">Execute on-chain</div>
          <HedgeForm balances={balances} defaultSolAmount={hedgeSolAmount} />
        </div>
      ) : stageKey === 'LP' ? (
        <div className="rounded-xl border border-white/5 bg-surface-card p-4">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-3">Execute on-chain</div>
          <LpForm balances={balances} positions={lpPositions ?? []} lpStage={stage} />
        </div>
      ) : NativeForm ? (
        <div className="rounded-xl border border-white/5 bg-surface-card p-4">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-3">Execute on-chain</div>
          <NativeForm balances={balances} defaultAmount={prePopulatedAmount} />
        </div>
      ) : recipe?.protocolUrl && !iframeBlocked ? (
        <div className="rounded-xl border border-white/5 bg-surface-card p-4">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-3">Execute on-chain</div>
          <div className="relative rounded-lg border border-white/10 overflow-hidden bg-[#0a0d14]" style={{ height: 480 }}>
            {!iframeLoaded && (
              <div className="absolute inset-0 flex items-center justify-center text-[11px] text-gray-500">
                Loading {recipe.protocolLabel}…
              </div>
            )}
            <iframe
              src={recipe.protocolUrl}
              title={recipe.protocolLabel}
              className="w-full h-full border-0"
              onLoad={() => setIframeLoaded(true)}
              onError={() => setIframeBlocked(true)}
              allow="clipboard-write; clipboard-read"
            />
          </div>
        </div>
      ) : recipe?.protocolUrl ? (
        <a
          href={recipe.protocolUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-accent-blue/10 border border-accent-blue/20 text-accent-blue text-sm font-semibold hover:bg-accent-blue/20 transition-colors"
        >
          Open {recipe.protocolLabel}
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-3.5 h-3.5"><path d="M7 2H2v12h12V9M9 2h5m0 0v5m0-5L7 10" /></svg>
        </a>
      ) : null}

      {/* Risk callout */}
      {recipe?.risk && (
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-amber-300 mb-1.5">Key risk</div>
          <p className="text-[12px] text-amber-100/80 leading-relaxed">{recipe.risk}</p>
        </div>
      )}

      {/* Navigation */}
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-white/10 text-gray-300 text-sm hover:bg-white/5 transition-colors"
        >
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-3.5 h-3.5"><polyline points="10 3 5 8 10 13" /></svg>
          Back
        </button>
        <button
          type="button"
          onClick={onNext}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-accent-purple text-white font-semibold text-sm hover:opacity-90 transition-opacity"
        >
          {isLast ? 'See Summary' : 'Mark done & Next'}
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-4 h-4"><polyline points="6 3 11 8 6 13" /></svg>
        </button>
      </div>
    </div>
  )
}
